import React from 'react';
import { Alert, Button, Row, Space } from 'antd';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { CheckCircleFilled } from '@ant-design/icons';
import { StripeAccountStatus } from 'utils/apiClient/services/shorepay/types';
import bannerImg from 'assets/images/shorepay_welcome_banner.png';
import { useLocalStorageState, useToggle } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { AccountErrorResponse } from 'utils/apiClient/services/core/types';
import { useAccount, useCreateAccount } from '../hooks/account';

const ContentWrapper: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Row justify="center" css="padding: 32px 24px">
    <Space direction="vertical" css="width: 570px" size={32}>
      {children}
    </Space>
  </Row>
);

function getAlertProps(status: StripeAccountStatus | undefined) {
  switch (status) {
    case 'Rejected':
      return {
        type: 'error' as const,
        message: defineMessage({
          defaultMessage:
            'There was an error during the signup. Please contact support...',
        }),
      };

    case 'Restricted':
      return {
        type: 'warning' as const,
        message: defineMessage({
          defaultMessage:
            'The sign up was not completed. Click the button below to continue',
        }),
      };

    default:
      return undefined;
  }
}

const TERMS_AND_CONDITIONS_DE = 'https://www.shore.com/de/rechtliches/';
const TERMS_AND_CONDITIONS_FALLBACK = 'https://www.shore.com/en/legal-note/';

const AccountOnboarding: React.FC = () => {
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const intl = useIntl();
  const accQuery = useAccount();
  const signUpMutation = useCreateAccount();
  const [btnClicked, { toggle: toggleBtn }] = useToggle(false);

  const { stripe_account_status: status } = accQuery.data ?? {};

  const accountNotFound =
    (accQuery.error as AccountErrorResponse)?.status_code === 404;

  const alertProps = getAlertProps(status);

  // Determine currency based on country
  const currency = account?.country === 'CH' ? 'CHF' : '€';
  const price = '69';

  const content = (
    <>
      <Row>
        <h1 css="font-size: 24px; margin: 0">
          <FormattedMessage defaultMessage="Easily collect payments anytime, anywhere" />
        </h1>
      </Row>

      <Row>
        <img
          src={bannerImg}
          alt="welcome banner"
          css="height: 154px; width: 568px"
        />
      </Row>

      <Row>
        <FormattedMessage defaultMessage="Collect payments effortlessly and safely anytime. With Shore Pay you offer your customer all common card payments - and contactless or with their smartphone too." />
      </Row>

      <Row>
        <FormattedMessage
          defaultMessage="<li>{check_icon}no monthly fee, no set up fee</li> <li>{check_icon}no cancellation needed - just don't use it if you don't want to</li> <li>{check_icon}Transparent fees: 1.35% transaction fee on all payments</li> <li>{check_icon}compact card reader for {price}{currency}. Order it now through our <shopLink>hardware-shop</shopLink></li> <li>{check_icon}accept all common card types, as well as GooglePay and Apple Pay.</li> <li>{check_icon}conform to all legal requirements and the highest security standards.</li>"
          values={{
            li: (...v: React.ReactNode[]) => (
              <span
                css={`
                  display: inline-block;
                  width: 100%;
                  line-height: 25px;
                `}
              >
                {v}
              </span>
            ),
            breakline: <br />,
            shopLink: (...txt: React.ReactNode[]) => (
              <a
                href="https://shop.shore.com/de/shop/shore-kartenlesegeraet/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {txt}
              </a>
            ),
            check_icon: (
              <CheckCircleFilled css="margin-right: 10px; color: #54B25C" />
            ),
            price,
            currency,
          }}
        />
      </Row>
    </>
  );

  if (
    accountNotFound ||
    (status && status !== 'Complete' && status !== 'Enabled')
  ) {
    return (
      <ContentWrapper>
        {alertProps && (
          <Alert
            closable
            showIcon
            type={alertProps.type}
            message={intl.formatMessage(alertProps.message)}
          />
        )}

        {content}

        {accountNotFound ? (
          <>
            <Row justify="center">
              <Button
                data-testid="signup-btn"
                type="primary"
                loading={signUpMutation.isPending && btnClicked}
                onClick={() => {
                  toggleBtn();
                  signUpMutation.mutate();
                }}
              >
                <FormattedMessage defaultMessage="Sign Up Now!" />
              </Button>
            </Row>
            <Row justify="center">
              <FormattedMessage
                defaultMessage="By signing up for Shore Pay you accept these terms. <link>Read more</link>"
                values={{
                  link: (...txt: React.ReactNode[]) => (
                    <a
                      href={
                        account?.country === 'DE'
                          ? TERMS_AND_CONDITIONS_DE
                          : TERMS_AND_CONDITIONS_FALLBACK
                      }
                      css="margin-left:5px"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {txt}
                    </a>
                  ),
                }}
              />
            </Row>
          </>
        ) : (
          <Row justify="center">
            <Button
              type="primary"
              loading={btnClicked}
              href={accQuery.data?.account_link?.url}
              disabled={accQuery.data?.stripe_account_status === 'Rejected'}
              onClick={() => toggleBtn()}
            >
              <FormattedMessage defaultMessage="Complete Sign Up Now!" />
            </Button>
          </Row>
        )}
      </ContentWrapper>
    );
  }
  return null;
};

export default AccountOnboarding;
